import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, apiFetch, useSocket } from '../App';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';


const CATS = ['All','Politics','Sports','Economy','Entertainment','World'];
const CAT_COLORS = {
  Politics:'#0e8fff', Sports:'#00c566', Economy:'#f5c842',
  Entertainment:'#ff6b35', World:'#0fb8a8'
};

function MarketCard({ market, onStake, onDetail }) {
  const { user } = useAuth();
  const yesOdds = market.yes_odds || 50;
  const noOdds  = market.no_odds  || 50;
  const vol     = market.volume   || 0;
  const color   = CAT_COLORS[market.category] || '#ff6b35';
  const daysLeft = Math.ceil((new Date(market.ends_at) - new Date()) / 86400000);

  return (
    <div className="card" style={{ cursor:'pointer', transition:'border-color .2s, transform .2s', height:'100%', display:'flex', flexDirection:'column' }}
      onClick={() => onDetail(market)}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,107,53,.3)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
        <span className="tag" style={{ background: color+'15', color }}>{market.category}</span>
        <span style={{ fontSize:11, color:'var(--muted)' }}>{daysLeft > 0 ? `${daysLeft}d left` : 'Closing soon'}</span>
      </div>

      {/* Title */}
      <div style={{ fontSize:15, fontWeight:600, lineHeight:1.4, marginBottom:12, color:'var(--text)' }}>{market.title}</div>

      {/* Description */}
      {market.description && (
        <div style={{ fontSize:11, color:'#f5c842', background:'rgba(245,200,66,.06)', border:'1px solid rgba(245,200,66,.15)', borderRadius:8, padding:'6px 10px', marginBottom:12, lineHeight:1.5 }}>
          📋 {market.description.replace('Resolution criteria: ','')}
        </div>
      )}

      {/* Odds bar */}
      <div style={{ marginBottom:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text2)', marginBottom:4 }}>
          <span>YES {yesOdds}%</span>
          <span>NO {noOdds}%</span>
        </div>
        <div style={{ height:6, background:'var(--bg3)', borderRadius:3, overflow:'hidden', display:'flex' }}>
          <div style={{ height:'100%', width:`${yesOdds}%`, background:'linear-gradient(90deg,#00c566,#0fb8a8)', transition:'width .4s' }}/>
          <div style={{ height:'100%', width:`${noOdds}%`, background:'linear-gradient(90deg,#ff6b35,#ff4444)', transition:'width .4s' }}/>
        </div>
      </div>

      {/* Footer */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'auto', paddingTop:10 }}>
        <div style={{ fontSize:11, color:'var(--muted)' }}>⭐ {vol} staked <span style={{color:'var(--muted)'}}>≈ ₦{(vol*15).toLocaleString()}</span></div>
        {user ? (
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={e => { e.stopPropagation(); onStake(market,'YES'); }} style={{ padding:'6px 16px', borderRadius:8, border:'none', background:'rgba(0,197,102,.1)', color:'#00c566', fontSize:12, fontWeight:700, cursor:'pointer', transition:'background .15s' }}
              onMouseEnter={e=>e.target.style.background='rgba(0,197,102,.2)'}
              onMouseLeave={e=>e.target.style.background='rgba(0,197,102,.1)'}>
              YES ↑
            </button>
            <button onClick={e => { e.stopPropagation(); onStake(market,'NO'); }} style={{ padding:'6px 16px', borderRadius:8, border:'none', background:'rgba(255,107,53,.1)', color:'#ff6b35', fontSize:12, fontWeight:700, cursor:'pointer', transition:'background .15s' }}
              onMouseEnter={e=>e.target.style.background='rgba(255,107,53,.2)'}
              onMouseLeave={e=>e.target.style.background='rgba(255,107,53,.1)'}>
              NO ↓
            </button>
          </div>
        ) : (
          <span style={{ fontSize:11, color:'var(--muted)' }}>Login to stake</span>
        )}
      </div>
    </div>
  );
}

function StakeModal({ market, side, onClose, onSuccess }) {
  const { user, setUser, showToast } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  if (!user) { navigate('/auth'); return null; }
  const [loading, setLoading] = useState(false);

  const yesOdds = market.yes_odds || 50;
  const noOdds  = market.no_odds  || 50;
  const mySideOdds = side === 'YES' ? yesOdds : noOdds;
  const mySideVol  = side === 'YES' ? (market.yes_volume||0) : (market.no_volume||0);
  const oppVol     = side === 'YES' ? (market.no_volume||0)  : (market.yes_volume||0);
  const amt        = parseInt(amount) || 0;
  const newMySide  = mySideVol + amt;
  const newTotal   = (market.volume||0) + amt;
  const rawEst     = oppVol <= 0 ? amt : Math.round((amt/newMySide)*newTotal*0.95);
  const minGuarantee = Math.ceil(amt * 1.10);
  const est        = Math.max(rawEst, minGuarantee);
  const profit     = est - amt;
  const pct        = amt > 0 ? Math.round((profit/amt)*100) : 0;
  const isLowOdds  = oppVol > 0 && rawEst < minGuarantee;

  async function confirmStake() {
    if (!amt || amt < 10) return showToast('Minimum stake is 10 Stars', 'error');
    if (amt > user.balance) return showToast('Insufficient balance', 'error');
    setLoading(true);
    try {
      const res = await apiFetch('/api/web/stake', { method:'POST', body:{ marketId:market.id, side, amount:amt }});
      setUser(u => ({ ...u, balance: res.newBalance }));
      showToast(`Staked ${amt} ⭐ on ${side}!`, 'success');
      onSuccess();
    } catch(err) { showToast(err.message, 'error'); }
    setLoading(false);
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="card" style={{ width:'100%', maxWidth:420 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, letterSpacing:1 }}>PLACE STAKE</div>
          <button onClick={onClose} style={{ background:'var(--bg3)', border:'none', color:'var(--text2)', borderRadius:'50%', width:28, height:28, fontSize:14 }}>✕</button>
        </div>

        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16, lineHeight:1.5 }}>{market.title}</div>

        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          <div style={{ flex:1, textAlign:'center', padding:'10px', borderRadius:10, background: side==='YES'?'rgba(0,197,102,.12)':'var(--bg2)', border:`1px solid ${side==='YES'?'#00c566':'var(--border)'}` }}>
            <div style={{ fontSize:11, color:'var(--muted)' }}>YOUR PICK</div>
            <div style={{ fontSize:18, fontWeight:700, color: side==='YES'?'#00c566':'#ff6b35' }}>{side}</div>
            <div style={{ fontSize:11, color:'var(--muted)' }}>{mySideOdds}% odds</div>
          </div>
          <div style={{ flex:1, textAlign:'center', padding:'10px', borderRadius:10, background:'var(--bg2)', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:11, color:'var(--muted)' }}>BALANCE</div>
            <div style={{ fontSize:18, fontWeight:700, color:'#f5c842' }}>{user.balance} ⭐</div>
          <div style={{ fontSize:10, color:'var(--muted)' }}>≈ ₦{(user.balance*15).toLocaleString()}</div>
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, color:'var(--text2)', fontWeight:600, marginBottom:6 }}>STAKE AMOUNT (Stars)</div>
          <input type="number" placeholder="Enter amount (min 50 ⭐)" value={amount} onChange={e=>setAmount(e.target.value)} min={50} max={user.balance}
            style={{ width:'100%', padding:'12px 14px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text)', fontSize:16, outline:'none' }}/>
          <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
            {[10,25,50,100,250,500].map(v => (
              <button key={v} onClick={()=>setAmount(String(Math.min(v,user.balance)))}
                disabled={user.balance < v}
                style={{ flex:1, minWidth:50, padding:'7px 4px', background: parseInt(amount)===v?'rgba(255,107,53,.15)':'var(--bg2)', border:`1px solid ${parseInt(amount)===v?'var(--orange)':'var(--border)'}`, borderRadius:8, color:parseInt(amount)===v?'var(--orange)':'var(--text2)', fontSize:12, fontWeight:parseInt(amount)===v?700:400, cursor:'pointer', opacity:user.balance<v?.4:1 }}>
                {v}⭐
              </button>
            ))}
          </div>
          <button onClick={()=>setAmount(String(user.balance))} style={{ width:'100%', marginTop:6, padding:'6px', background:'rgba(245,200,66,.06)', border:'1px solid rgba(245,200,66,.2)', borderRadius:8, color:'var(--gold)', fontSize:11, cursor:'pointer', fontWeight:600 }}>
            MAX ({user.balance} ⭐)
          </button>
        </div>

        {amt > 0 && (
          <div style={{ background:'var(--bg2)', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
            {isLowOdds && <div style={{ fontSize:11, color:'#f5c842', marginBottom:8, background:'rgba(245,200,66,.08)', borderRadius:6, padding:'6px 8px' }}>
              ⚠️ Low odds — platform guarantees min 10% profit if you win.
            </div>}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
              <span style={{ color:'var(--text2)' }}>Potential win</span>
              <span style={{ fontWeight:700, color: isLowOdds?'#f5c842':'#00c566' }}>{est} ⭐ (+{pct}%{isLowOdds?' guaranteed':''}) <span style={{fontSize:11,color:'var(--muted)'}}>≈ ₦{(est*15).toLocaleString()}</span></span>
            </div>
          </div>
        )}

        <button className="btn-primary" onClick={confirmStake} disabled={loading||!amt||amt<10||amt>user.balance} style={{ width:'100%', padding:14, fontSize:15 }}>
          {loading ? 'Placing stake...' : `Stake ${amt||0} ⭐ on ${side}`}
        </button>
      </div>
    </div>
  );
}

function DetailModal({ market, onClose, onStake }) {
  const [sparkline, setSparkline] = React.useState([]);
  React.useEffect(() => {
    apiFetch('/api/web/markets/' + market.id + '/sparkline')
      .then(setSparkline).catch(()=>{});
  }, [market.id]);
  const yesOdds = market.yes_odds || 50;
  const noOdds  = market.no_odds  || 50;
  const vol     = market.volume   || 0;
  const daysLeft = Math.ceil((new Date(market.ends_at) - new Date()) / 86400000);
  const color   = { Politics:'#0e8fff', Sports:'#00c566', Economy:'#f5c842', Entertainment:'#ff6b35', World:'#0fb8a8' }[market.category] || '#ff6b35';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.8)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="card" style={{ width:'100%', maxWidth:520, maxHeight:'85vh', overflowY:'auto' }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <span className="tag" style={{ background:color+'15', color }}>{market.category}</span>
          <button onClick={onClose} style={{ background:'var(--bg3)', border:'none', color:'var(--text2)', borderRadius:'50%', width:28, height:28, fontSize:14, cursor:'pointer' }}>✕</button>
        </div>

        <div style={{ fontSize:17, fontWeight:700, lineHeight:1.4, marginBottom:8 }}>{market.title}</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginBottom:16 }}>
          Closes {new Date(market.ends_at).toLocaleDateString('en-NG', { day:'numeric', month:'short', year:'numeric' })} · {daysLeft > 0 ? `${daysLeft} days left` : 'Closing soon'}
        </div>

        {/* Sparkline Chart */}
        {(()=>{
          const W=500,H=90,P=10;
          const vals = sparkline.length >= 2 ? sparkline.map(p=>p.yes_pct) : null;
          if (!vals) return (
            <div style={{height:90,background:'var(--bg2)',borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:16,border:'1px solid var(--border)'}}>
              <span style={{fontSize:12,color:'var(--muted)'}}>Chart appears after 2+ stakes</span>
            </div>
          );
          const mn=Math.max(0,Math.min(...vals)-5),mx=Math.min(100,Math.max(...vals)+5),rng=mx-mn||1;
          const pts=vals.map((v,i)=>`${P+(i/(vals.length-1))*(W-P*2)},${H-P-((v-mn)/rng)*(H-P*2)}`).join(' ');
          const fill=`${P},${H} ${pts} ${W-P},${H}`;
          const last=vals[vals.length-1],first=vals[0];
          const diff=last-first;
          const col=diff>0?'#00c566':diff<0?'#ff6b35':'#7b8cde';
          const arrow=diff>0?'↑':diff<0?'↓':'→';
          const diffLabel=diff>0?`+${diff}%`:diff<0?`${diff}%`:'Steady';
          return (
            <div style={{background:'var(--bg2)',borderRadius:12,padding:'12px 14px',marginBottom:16,border:'1px solid var(--border)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <div>
                  <span style={{fontSize:11,color:'var(--muted)',fontWeight:600,letterSpacing:.5}}>YES ODDS CHART</span>
                  <span style={{fontSize:10,color:'var(--muted)',marginLeft:8}}>({vals.length} stakes)</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:18}}>{arrow}</span>
                  <span style={{fontSize:14,color:col,fontWeight:700}}>{diffLabel}</span>
                </div>
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:90,display:'block'}}>
                <defs>
                  <linearGradient id="sfweb" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={col} stopOpacity=".3"/>
                    <stop offset="100%" stopColor={col} stopOpacity=".02"/>
                  </linearGradient>
                </defs>
                {[25,50,75].map(pct=>{
                  const y=H-P-((pct-mn)/rng)*(H-P*2);
                  return y>=P&&y<=H-P?(
                    <g key={pct}>
                      <line x1={P} x2={W-P} y1={y} y2={y} stroke="#2a2a2a" strokeWidth=".5" strokeDasharray="3,3"/>
                      <text x={P+2} y={y-3} fill="#4a5a63" fontSize="8">{pct}%</text>
                    </g>
                  ):null;
                })}
                <polygon points={fill} fill="url(#sfweb)"/>
                <polyline points={pts} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
                {vals.map((v,i)=>{
                  const x=P+(i/(vals.length-1))*(W-P*2),y=H-P-((v-mn)/rng)*(H-P*2);
                  return <circle key={i} cx={x} cy={y} r="3" fill={col}/>;
                })}
              </svg>
              <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontSize:11}}>
                <span style={{color:'var(--muted)'}}>YES started at <strong style={{color:'var(--text)'}}>{first}%</strong></span>
                <span style={{color:col,fontWeight:700}}>Now <strong>{last}%</strong></span>
              </div>
            </div>
          );
        })()}

        {/* Resolution criteria */}
        {market.description && (
          <div style={{ background:'rgba(245,200,66,.06)', border:'1px solid rgba(245,200,66,.2)', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#f5c842', lineHeight:1.6 }}>
            📋 <strong>Resolution criteria:</strong> {market.description.replace('Resolution criteria: ','')}
          </div>
        )}

        {/* Odds visual */}
        <div style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:24, fontWeight:700, color:'#00c566', fontFamily:"'Bebas Neue',sans-serif" }}>{yesOdds}%</div>
              <div style={{ fontSize:11, color:'var(--muted)' }}>YES chance</div>
            </div>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:18, fontWeight:700, color:'var(--text2)' }}>⭐ {vol}</div>
              <div style={{ fontSize:13, color:'#f5c842', fontWeight:700 }}>≈ ₦{(vol*15).toLocaleString()}</div>
              <div style={{ fontSize:11, color:'var(--muted)' }}>Total staked</div>
            </div>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:24, fontWeight:700, color:'#ff6b35', fontFamily:"'Bebas Neue',sans-serif" }}>{noOdds}%</div>
              <div style={{ fontSize:11, color:'var(--muted)' }}>NO chance</div>
            </div>
          </div>
          <div style={{ height:10, background:'var(--bg3)', borderRadius:5, overflow:'hidden', display:'flex' }}>
            <div style={{ height:'100%', width:`${yesOdds}%`, background:'linear-gradient(90deg,#00c566,#0fb8a8)', transition:'width .4s' }}/>
            <div style={{ height:'100%', width:`${noOdds}%`, background:'linear-gradient(90deg,#ff6b35,#ff4444)', transition:'width .4s' }}/>
          </div>
        </div>

        {/* Pool breakdown */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
          <div style={{ background:'rgba(0,197,102,.06)', border:'1px solid rgba(0,197,102,.2)', borderRadius:10, padding:'12px', textAlign:'center' }}>
            <div style={{ fontSize:11, color:'#00c566', marginBottom:4 }}>YES POOL</div>
            <div style={{ fontSize:18, fontWeight:700, color:'#00c566' }}>{Math.round((market.volume||0)*yesOdds/100)} ⭐</div>
            <div style={{ fontSize:10, color:'var(--muted)' }}>≈ ₦{(Math.round((market.volume||0)*yesOdds/100)*15).toLocaleString()}</div>
          </div>
          <div style={{ background:'rgba(255,107,53,.06)', border:'1px solid rgba(255,107,53,.2)', borderRadius:10, padding:'12px', textAlign:'center' }}>
            <div style={{ fontSize:11, color:'#ff6b35', marginBottom:4 }}>NO POOL</div>
            <div style={{ fontSize:18, fontWeight:700, color:'#ff6b35' }}>{Math.round((market.volume||0)*noOdds/100)} ⭐</div>
            <div style={{ fontSize:10, color:'var(--muted)' }}>≈ ₦{(Math.round((market.volume||0)*noOdds/100)*15).toLocaleString()}</div>
          </div>
        </div>

        {/* Stake buttons */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <button onClick={() => { onClose(); onStake(market,'YES'); }}
            style={{ padding:'14px', background:'rgba(0,197,102,.1)', border:'2px solid #00c566', borderRadius:12, color:'#00c566', fontSize:15, fontWeight:700, cursor:'pointer' }}>
            YES ↑ ({yesOdds}%)
          </button>
          <button onClick={() => { onClose(); onStake(market,'NO'); }}
            style={{ padding:'14px', background:'rgba(255,107,53,.1)', border:'2px solid #ff6b35', borderRadius:12, color:'#ff6b35', fontSize:15, fontWeight:700, cursor:'pointer' }}>
            NO ↓ ({noOdds}%)
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MarketsPage() {
  const [markets, setMarkets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [cat, setCat]           = useState('All');
  const [search, setSearch]     = useState('');
  const [stakeModal, setStakeModal]   = useState(null);
  const [disputeModal, setDisputeModal] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const socket = useSocket();

  // Real-time market odds updates
  useEffect(() => {
    if (!socket) return;
    const handler = ({ id, yes, no, volume }) => {
      setMarkets(prev => prev.map(m => m.id === id
        ? { ...m, yes_odds: yes, no_odds: no, volume }
        : m
      ));
    };
    socket.on('market:odds', handler);
    return () => socket.off('market:odds', handler);
  }, [socket]);

  // Real-time market resolved
  useEffect(() => {
    if (!socket) return;
    const handler = ({ id, result }) => {
      setMarkets(prev => prev.map(m => m.id === id
        ? { ...m, status:'resolved', result }
        : m
      ).filter(m => m.status === 'open'));
    };
    socket.on('market:resolved', handler);
    return () => socket.off('market:resolved', handler);
  }, [socket]);

  // Real-time new market
  useEffect(() => {
    if (!socket) return;
    const handler = (market) => {
      setMarkets(prev => [market, ...prev]);
    };
    socket.on('market:new', handler);
    return () => socket.off('market:new', handler);
  }, [socket]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cat !== 'All') params.set('category', cat);
      if (search) params.set('search', search);
      const data = await apiFetch('/api/web/markets?' + params);
      // Pin market with highest pool at top
      data.sort((a,b) => (b.volume||0) - (a.volume||0));
      setMarkets(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [cat, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {/* Dispute Modal */}
      {disputeModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:100, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
          onClick={e => e.target===e.currentTarget && setDisputeModal(null)}>
          <div style={{ background:'var(--bg1)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:520, padding:'24px 20px 34px', border:'1px solid var(--border)', borderBottom:'none' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:4 }}>⚠️ Dispute Resolution</div>
            <div style={{ fontSize:12, color:'var(--text2)', marginBottom:16, lineHeight:1.6 }}>
              <strong>{disputeModal.title}</strong><br/>
              Result: <span style={{ color: disputeModal.result==='YES'?'#00c566':'#ff6b35', fontWeight:600 }}>{disputeModal.result}</span>
            </div>
            <label style={{ fontSize:11, color:'var(--text2)', fontWeight:600, letterSpacing:.5 }}>REASON FOR DISPUTE</label>
            <textarea
              placeholder="Explain why you believe this market was resolved incorrectly..."
              value={disputeReason}
              onChange={e => setDisputeReason(e.target.value)}
              rows={4}
              style={{ width:'100%', marginTop:6, marginBottom:8, padding:'12px 14px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text)', fontSize:13, outline:'none', resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
            />
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:16 }}>{disputeReason.length}/500 characters · Min 10</div>
            <button className="btn-primary" onClick={submitDispute}
              disabled={disputeLoading || disputeReason.length < 10}
              style={{ width:'100%', padding:13, fontSize:14, marginBottom:10 }}>
              {disputeLoading ? 'Submitting...' : 'Submit Dispute'}
            </button>
            <button onClick={() => setDisputeModal(null)}
              style={{ width:'100%', padding:10, background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:13 }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{ marginBottom:24, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:1.5, marginBottom:4 }}>LIVE MARKETS</div>
          <div style={{ fontSize:13, color:'var(--text2)' }}>Predict YES or NO — earn Stars when you're right</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(0,197,102,.08)', border:'1px solid rgba(0,197,102,.2)', borderRadius:20, padding:'4px 10px' }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background: socket ? '#00c566' : '#ff4444', animation: socket ? 'pulse 2s infinite' : 'none' }}/>
          <span style={{ fontSize:11, color: socket ? '#00c566' : '#ff4444', fontWeight:600 }}>{socket ? 'LIVE' : 'OFFLINE'}</span>
        </div>
      </div>
      <style>{'@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}'}</style>

      {/* Search */}
      <div style={{ position:'relative', marginBottom:14 }}>
        <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', display:'flex' }}><Search size={16} strokeWidth={1.8}/></span>
        <input type="text" placeholder="Search markets..." value={search} onChange={e=>{setSearch(e.target.value);setCat('All');}}
          style={{ width:'100%', padding:'10px 12px 10px 38px', background:'var(--bg1)', border:`1px solid ${search?'var(--orange)':'var(--border)'}`, borderRadius:12, color:'var(--text)', fontSize:13, outline:'none' }}/>
        {search && <button onClick={()=>setSearch('')} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'var(--muted)', cursor:'pointer', display:'flex' }}><X size={16} strokeWidth={1.8}/></button>}
      </div>

      {/* Category tabs */}
      <div style={{ display:'flex', gap:6, overflowX:'auto', marginBottom:20, paddingBottom:4 }}>
        {CATS.map(c => (
          <button key={c} onClick={()=>{setCat(c);setSearch('');}}
            style={{ flexShrink:0, padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
              background: cat===c ? 'var(--orange)' : 'var(--bg1)',
              color: cat===c ? '#000' : 'var(--text2)',
              transition:'all .15s' }}>
            {c}
          </button>
        ))}
      </div>

      {/* Markets */}
      {loading ? (
        <div style={{ display:'grid', gap:14 }}>
          {[1,2,3].map(i => <div key={i} className="card" style={{ height:150, background:'var(--bg2)', animation:'pulse 1.5s infinite' }}/>)}
          <style>{'@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}'}</style>
        </div>
      ) : markets.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'var(--muted)' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📊</div>
          <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>No markets found</div>
          <div style={{ fontSize:13 }}>Try a different category or search</div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize:12, color:'var(--muted)', marginBottom:12 }}>{markets.length} market{markets.length!==1?'s':''} {search?`for "${search}"`:''}</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
            {markets.map(m => <MarketCard key={m.id} market={m} onStake={(mkt,side) => setStakeModal({market:mkt,side})} onDetail={mkt => setDetailModal(mkt)}/>)}
          </div>
        </div>
      )}

      {detailModal && (
        <DetailModal market={detailModal} onClose={() => setDetailModal(null)} onStake={(mkt,side) => { setDetailModal(null); setStakeModal({market:mkt,side}); }}/>
      )}

      {stakeModal && (
        <StakeModal market={stakeModal.market} side={stakeModal.side}
          onClose={() => setStakeModal(null)}
          onSuccess={() => { setStakeModal(null); load(); }}/>
      )}
    </div>
  );
}
